import React, { useCallback, useEffect } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { usePrevious } from '_hooks';
import { selectSubscriptionsCanceling, selectSubscriptionsError } from '_store/billing/selector';
import { cancelSubscription } from '_store/billing/slice';
import { selectModalDataByKey, selectModalVisibleByKey } from '_store/modals/selector';
import { hideModal } from '_store/modals/slice';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/shared/Button';
import Icon from 'components/shared/Icon';
import Spinner from 'components/shared/Spinner';
import Text from 'components/shared/layouts/Text';

const StyledModalBody = styled(Modal.Body)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.large}px;
`;

const StyledCloseActionContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: ${-theme.spaces.large3X}px;
  right: 0;
  font-family: 'RethinkSans';
  font-style: normal;
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 400;
  color: ${theme.colors.white};
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.4;
    `}
`;

const StyledIconContainer = styled.div`
  margin-right: ${theme.spaces.small2X}px;
`;

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
  min-height: 42px;
`;

const StyledUnsubscribeButton = styled(Button)`
  @media ${theme.devices.tablet} {
    margin-bottom: ${theme.spaces.regular}px;
  }
`;

const StyledRow = styled(Row)`
  width: 100%;
`;

const CancelSubscriptionModal = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectModalVisibleByKey(MODAL_KEYS.cancelSubscriptionConfirm));
  const data = useSelector(selectModalDataByKey(MODAL_KEYS.cancelSubscriptionConfirm));
  const loading = useSelector(selectSubscriptionsCanceling);
  const prevLoading = usePrevious(loading);
  const error = useSelector(selectSubscriptionsError);
  const customerId = useSelector(selectCustomerId);

  const { orderId, productId, productName } = data || {};

  const handleClose = () => {
    dispatch(hideModal({ key: MODAL_KEYS.cancelSubscriptionConfirm }));
  };

  const handleUnsubscribe = useCallback(() => {
    if (customerId && orderId && productId && productName) {
      dispatch(cancelSubscription({ customerId, orderId, productId, productName }));
    }
  }, [dispatch, customerId, orderId, productId, productName]);

  useEffect(() => {
    if (prevLoading && !loading && !error) {
      dispatch(hideModal({ key: MODAL_KEYS.cancelSubscriptionConfirm }));
    }
  }, [dispatch, prevLoading, loading, error]);

  return (
    <Modal
      dialogClassName="cancel-subscription-modal"
      show={visible}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <StyledModalBody>
        <StyledCloseActionContainer disabled={loading} onClick={handleClose}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.close} color={theme.colors.white} />
          </StyledIconContainer>
          Close
        </StyledCloseActionContainer>
        <Text
          size={theme.fontSizes.largeX}
          weight={700}
          color={theme.colors.deepBlueGreen}
          bottomSpace={theme.spaces.medium}
        >
          Cancel Subscription
        </Text>
        <StyledRow>
          <Col xs={12}>
            <Text bottomSpace={theme.spaces.large2X}>
              Are you sure you wish to cancel your {productName} subscription?
            </Text>
          </Col>
          <Col xs={12} md={6}>
            <StyledUnsubscribeButton disabled={loading} color={theme.colors.red.primary} onClick={handleUnsubscribe}>
              Unsubscribe
            </StyledUnsubscribeButton>
          </Col>
          <Col xs={12} md={6}>
            <Button disabled={loading} color={theme.colors.gray.secondary} onClick={handleClose}>
              Never mind
            </Button>
          </Col>
          <Col xs={12}>
            {loading && (
              <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
                <StyledMessageContainer>
                  <Spinner />
                  <div>Updating...</div>
                </StyledMessageContainer>
              </Text>
            )}
          </Col>
        </StyledRow>
      </StyledModalBody>
    </Modal>
  );
};

export default CancelSubscriptionModal;
